import { Switch } from '@components/core-ui/form-elements/Switch';
import PencilIcon from '@components/icons/Pencil.svg';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { EntityIconProfile } from '@components/core-ui/EntityIcon/variations/EntityIconProfile';
import { GetUserDetailsResultDto } from 'app/API';
import { AvatarBlock } from './styles/AvatarBlock';
import { InputBlockWrapper } from './styles/InputBlockWrapper';
import { InputWrapper } from './styles/InputWrapper';
import { LeftSide } from './styles/LeftSide';
import { RightSide } from './styles/RightSide';
import { RightSideColumns } from './styles/RightSideColumns';
import { Wrapper } from './styles/Wrapper';

type TProps = {
  user?: GetUserDetailsResultDto;
  reload: () => void;
  isUpdatingCanManageLayouts: boolean;
  onCanManageLayoutsChange: (membershipId: string, value: boolean) => void;
};

export const UserDetails = ({ user, reload, isUpdatingCanManageLayouts, onCanManageLayoutsChange }: TProps) => {
  const { openModal, closeModal } = useModalContext();
  const { organization } = useAuthContext();
  const userHasManageMembersPermission = organization?.permissions?.manageMembers;

  if (!user) {
    return null;
  }

  const orgRelation = (user.orgRelations ?? []).find((rel) => rel.slug === organization?.slug);

  const renderAvatar = () => {
    if (!user || !user.name || !user.lastname) {
      return '';
    }

    return (
      <AvatarBlock>
        <EntityIconProfile name={`${user.name} ${user.lastname}`} src={user.avatar ?? undefined} />
      </AvatarBlock>
    );
  };

  const editConcurrentLimit = () => {
    openModal(ModalNamesEnum.EditConcurrentJobsLimit, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      uid: user?.id,
      concurrentJobsLimit: user?.concurrentJobsLimit ?? 0,
    });
  };

  const changeCanManageLayouts = () => {
    onCanManageLayoutsChange(orgRelation?.membershipId ?? '', !orgRelation?.canManageLayouts);
  };

  return (
    <Wrapper>
      <LeftSide>
        <h3>General</h3>
      </LeftSide>

      <RightSide>
        {renderAvatar()}
        <RightSideColumns>
          <InputBlockWrapper>
            <InputWrapper>
              <div className="label">Name</div>
              <div className="value">
                {user.name} {user.lastname}
              </div>
            </InputWrapper>
            <InputWrapper>
              <div className="label">Email</div>
              <div className="value">{user.email}</div>
            </InputWrapper>
            <InputWrapper>
              <div className="label">Phone</div>
              <div className="value">{user.phone}</div>
            </InputWrapper>
            {userHasManageMembersPermission ? (
              <>
                <InputWrapper>
                  <div className="label">Concurrent jobs limit</div>
                  <div className="value">
                    {user.concurrentJobsLimit || 'Unlimited'}
                    {userHasManageMembersPermission ? (
                      <PencilIcon
                        className="pencil text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                        onClick={() => editConcurrentLimit()}
                      />
                    ) : null}
                  </div>
                </InputWrapper>

                <InputWrapper>
                  <div className="label">Can manage cloud layouts</div>
                  <div className="value">
                    <Switch
                      checked={orgRelation?.canManageLayouts}
                      onChange={changeCanManageLayouts}
                      disabled={!organization?.permissions?.manageOrganization || isUpdatingCanManageLayouts}
                    />
                  </div>
                </InputWrapper>
              </>
            ) : null}
          </InputBlockWrapper>
        </RightSideColumns>
      </RightSide>
    </Wrapper>
  );
};
